import { followLink, ctrlClickMac, ctrlClickWindows } from './ReactEditor.locstring.json';
import loc, { format } from 'owa-localize';
import { observer } from 'mobx-react';
import IEditor from './IEditor';
import EditorViewState from '../store/schema/EditorViewState';
import { attachDomEvent as attachDomEventForPrague } from '../utils/attachDomEventForPrague';
import getDefaultComposeFormat from '../utils/getDefaultComposeFormat';
import { register as registerLogOnFrameDropWhileTyping } from '../utils/logOnFrameDropWhileTyping';
import { getPerfData, triggerEventWithMeasure } from '../utils/measurePerf';
import { PerfData } from '../utils/PerfData';
import { createPluginUtils, EditorPluginEx } from '../utils/PluginUtils';
import { logUsage } from 'owa-analytics';
import { transformElementForDarkMode } from 'owa-dark-mode-utilities';
import emojiReplacements from 'owa-expressions-store/lib/utils/emojiReplacements';
import { isFeatureEnabled } from 'owa-feature-flags';
import ViewStateManagePlugin from '../internalPlugin/ViewStateManagePlugin';
import { isMac } from 'owa-user-agent/lib/userAgent';
import * as React from 'react';
import { Editor, EditorOptions, EditorPlugin, UndoService } from 'roosterjs-editor-core';
import { DefaultFormat } from 'roosterjs-editor-types';
import { AttributeCallbackMap, HtmlSanitizer } from 'roosterjs-html-sanitizer';
import {
    ContentEdit,
    CustomReplace,
    getDefaultContentEditFeatures,
    HyperLink,
    Paste,
    TableResize,
    Watermark,
} from 'roosterjs-editor-plugins';

export interface ReactEditorProps {
    viewState: EditorViewState;
    plugins?: EditorPlugin[];
    undo?: UndoService;
    isRtl?: boolean;
    classNames?: string;
    updateOnKeyPress?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    onDirectionChange?: (dirAttribute: 'ltr' | 'rtl') => void;
    showFormatPainterCursor?: boolean;
    watermark?: string;
    cssClassesToPreserveWhenPaste?: string[];
    logPerfDataCallback?: (perfData: PerfData) => void;
    ariaLabel?: string;
    isDarkMode?: boolean;
}

const FORMATPAINTERCURSOR_SVG = require('./formatpaintercursor.svg');
const styles = require('./ReactEditor.scss');
const classname = require('classnames/bind').bind(styles);
const WATERMARK_TEXT_FORMAT: DefaultFormat = {
    fontFamily: 'Segoe UI',
    fontSize: '14px',
    textColor: '#666', // $ms-color-neutralSecondary
};
const DARK_MODE_LINK_COLOR = 'rgb(228, 159, 255)';

@observer
export default class ReactEditor extends React.Component<ReactEditorProps, {}> implements IEditor {
    private editor: Editor;
    private editorDiv: HTMLDivElement;
    private contentEditPlugin: ContentEdit;
    private hyperLinkPlugin: HyperLink;
    private pastePlugin: Paste;
    private tableResizePlugin: TableResize;
    private customReplacePlugin: CustomReplace;
    private viewStateManagePlugin: ViewStateManagePlugin;
    private dirAttributeMutationObserver: MutationObserver = null;
    private unregisterLogOnFrameDropWhileTyping = null;

    constructor(props: ReactEditorProps) {
        super(props);

        const { viewState, updateOnKeyPress, cssClassesToPreserveWhenPaste } = props;

        let clickableLinkTooltip = format(
            loc(followLink),
            isMac() ? loc(ctrlClickMac) : loc(ctrlClickWindows)
        );
        let propertyCallbacks: AttributeCallbackMap = {
            originalsrc: this.keepValue, // For cut/paste text and image together, we need to preserve these props so that server side can handle it
            crossorigin: this.keepValue,
            contenttype: this.keepValue,
        };

        if (cssClassesToPreserveWhenPaste) {
            propertyCallbacks.class = this.cssClassCallback;
        }

        let contentEditFeatures = getDefaultContentEditFeatures();
        contentEditFeatures.smartOrderedList = true;
        contentEditFeatures.mergeInNewLineWhenBackspaceOnFirstChar = true;

        this.pastePlugin = new Paste(null /*deprecated*/, propertyCallbacks);
        this.contentEditPlugin = new ContentEdit(contentEditFeatures);
        this.hyperLinkPlugin = new HyperLink(href => clickableLinkTooltip + '\r\n' + href);
        this.tableResizePlugin = new TableResize();
        this.customReplacePlugin = new CustomReplace(emojiReplacements);
        this.viewStateManagePlugin = new ViewStateManagePlugin(viewState, updateOnKeyPress);
    }

    componentDidMount() {
        this.editor = new Editor(this.editorDiv, this.getEditorOptions());
        this.updateContentToViewState(true /*isInitializing*/);

        // Edge on windows allows the user to directly edit the DOM of the editing
        // container if they have both an rtl and ltr language installed. Capture
        // these edits and propagate them to the consumer.
        if (this.props.onDirectionChange !== undefined) {
            this.dirAttributeMutationObserver = new MutationObserver(this.onBrowserDirectionChange);
            this.dirAttributeMutationObserver.observe(this.editorDiv, {
                attributes: true,
                attributeFilter: ['dir'],
            });
        }

        if (isFeatureEnabled('cmp-logOnFrameDropWhileTyping')) {
            this.unregisterLogOnFrameDropWhileTyping = registerLogOnFrameDropWhileTyping(
                this.editorDiv,
                'ReactEditor'
            );
        }
    }

    componentWillUnmount() {
        if (this.editor) {
            if (this.props.logPerfDataCallback) {
                let perfData = getPerfData(this.editor);
                this.props.logPerfDataCallback(perfData);
            }
            this.editor.dispose();
        }

        this.editor = null;

        if (this.dirAttributeMutationObserver !== null) {
            this.dirAttributeMutationObserver.disconnect();
            this.dirAttributeMutationObserver = null;
        }

        if (this.unregisterLogOnFrameDropWhileTyping) {
            this.unregisterLogOnFrameDropWhileTyping();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: ReactEditorProps) {
        // If being provided a new view state, reset the content
        if (nextProps.viewState !== this.props.viewState) {
            this.setEditorContent(nextProps.viewState.content);
        }
        if (nextProps.isDarkMode !== this.props.isDarkMode) {
            this.setDarkMode(nextProps.isDarkMode);
        }
    }

    render() {
        let {
            onFocus,
            classNames,
            isRtl,
            showFormatPainterCursor,
            watermark,
            ariaLabel,
            isDarkMode,
        } = this.props;
        let style = showFormatPainterCursor
            ? {
                  cursor: `url("${FORMATPAINTERCURSOR_SVG}") 8.5 16, auto`,
              }
            : null;

        return (
            <div
                contentEditable={true}
                ref={ref => (this.editorDiv = ref)}
                dir={isRtl ? 'rtl' : 'ltr'}
                className={classname(classNames, styles.textSelectable, {
                    resetColors: !isDarkMode,
                })}
                onBlur={this.onBlur}
                onFocus={onFocus}
                style={style}
                role="textbox"
                aria-multiline="true"
                aria-label={ariaLabel ? ariaLabel : watermark}
            />
        );
    }

    public focus() {
        if (this.editor) {
            this.editor.focus();
        }
    }

    public getContent(): string {
        return this.editor.getContent();
    }

    public updateContentToViewState(isInitializing?: boolean) {
        this.viewStateManagePlugin.updateContentToViewState(isInitializing);
    }

    private externalContentTransform(element: HTMLElement) {
        const alteredElement = transformElementForDarkMode(
            element,
            isFeatureEnabled('cmp-darkModeSimpleRecolor')
        );
        if (!alteredElement && element.tagName == 'A') {
            // We will apply a color as a conveinence but only for dark mode.
            // When it's sent, this color will be stripped.
            element.style.color = DARK_MODE_LINK_COLOR;
        }
    }

    private getEditorOptions() {
        let { plugins, viewState, undo, watermark, logPerfDataCallback, isDarkMode } = this.props;
        let allPlugins: EditorPlugin[] = plugins ? [...plugins] : [];
        let defaultFormat = getDefaultComposeFormat();

        allPlugins = [
            ...allPlugins,
            this.contentEditPlugin,
            this.hyperLinkPlugin,
            this.pastePlugin,
            this.tableResizePlugin,
            this.customReplacePlugin,
            watermark && new Watermark(watermark, WATERMARK_TEXT_FORMAT),
            this.viewStateManagePlugin,
        ];

        let pluginUtils = createPluginUtils(this.pastePlugin);
        allPlugins.forEach(plugin => {
            let pluginEx = plugin as EditorPluginEx;
            if (pluginEx && pluginEx.setPluginUtils) {
                pluginEx.setPluginUtils(pluginUtils);
            }
        });

        let initialContent = HtmlSanitizer.convertInlineCss(viewState.content);
        let options: EditorOptions = {
            plugins: allPlugins,
            defaultFormat: defaultFormat,
            undo: undo,
            initialContent: initialContent,
            inDarkMode: isDarkMode,
            darkModeOptions:
                isDarkMode || isFeatureEnabled('cmp-darkModeToggle')
                    ? {
                          transformOnInitialize: true,
                          onExternalContentTransform: this.externalContentTransform,
                      }
                    : {},
            coreApiOverride: {
                ...(logPerfDataCallback
                    ? {
                          triggerEvent: triggerEventWithMeasure,
                      }
                    : {}),
                ...(isFeatureEnabled('cmp-prague')
                    ? {
                          attachDomEvent: attachDomEventForPrague,
                      }
                    : {}),
            },
        };

        return options;
    }

    private onBlur = () => {
        this.updateContentToViewState();
        if (this.props.onBlur) {
            this.props.onBlur();
        }
    };

    private cssClassCallback = (value: string) => {
        let values = value.split(' ');
        values = values.filter(
            value => this.props.cssClassesToPreserveWhenPaste.indexOf(value) >= 0
        );
        return values.length > 0 ? values.join(' ') : null;
    };

    private keepValue = (value: string) => value;

    private setEditorContent(content: string) {
        let contentAfterCSSInlining = HtmlSanitizer.convertInlineCss(content);
        this.editor.setContent(contentAfterCSSInlining, true /* triggerContentChangedEvent */);
        this.updateContentToViewState(true /*isInitializing*/);
    }

    private setDarkMode(nextDarkMode: boolean) {
        this.editor.setDarkModeState(nextDarkMode);
    }

    private onBrowserDirectionChange = (records: MutationRecord[]) => {
        const mutationRecord = records.length !== 0 ? records[0] : null;
        if (mutationRecord === null || mutationRecord.type !== 'attributes') {
            // This listener is only registered as listening to a single attribute on a single
            // element, so it should only ever receive one MutationRecord of type attribute
            logUsage(
                'direction_change_invalid_mutation_callback',
                records.map(record => JSON.stringify(record)),
                { isCore: true }
            );
            return;
        }

        // Cast here is sane because target of an attribute mutation is the HTML element
        // for which an attribute has changed
        const dirAttribute: string = (mutationRecord.target as HTMLElement).getAttribute('dir');

        // Filter direction changes to 'rtl' and 'ltr'. We explicitly do not support the 'auto' state.
        if (!dirAttribute || dirAttribute === 'auto') {
            this.props.onDirectionChange(null);
        } else if (dirAttribute === 'ltr' || dirAttribute === 'rtl') {
            this.props.onDirectionChange(dirAttribute);
        }
    };
}
